import { ConditionalDataOutput, ConditionalStep } from '@/types/automationSteps.ts';
import { Group, Stack } from '@mantine/core';
import * as React from 'react';
import StyledTextInput from '@/components/StyledInputFields/StyledTextInput.tsx';
import StyledSwitch from '@/components/StyledInputFields/StyledSwitch.tsx';
import useIsNewStep from '@/hooks/useIsNewStep.ts';
import StepFieldsRemoveButton from '@/pages/Editor/components/StepFieldsRemoveButton.tsx';
import { STEP_FIELD_MAX_LENGTH } from '@/utils/constants.ts';
import OutputPinButton from '@/pages/Editor/components/StepSidebar/OutputPinButton.tsx';
import useRetrieveLatestAutomationVersion from '@/hooks/api/automationVersions/useRetrieveLatestAutomationVersion.ts';
import JsonSchemaRefSelect from '@/pages/Editor/components/SharedFields/JsonSchemaRefSelect.tsx';
import { SchemaValidationMode } from '@/types/automationFields.ts';

type props = {
  conditionalDataOutput: ConditionalDataOutput;
  onChangeDelay: (updatedInput: ConditionalDataOutput) => void;
  onChangeNoDelay: (updatedInput: ConditionalDataOutput) => void;
  onDelete: () => void;
  step: ConditionalStep;
};

const ConditionalDataOutputFields = ({
  conditionalDataOutput,
  onChangeDelay,
  onChangeNoDelay,
  onDelete,
  step,
}: props) => {
  const isNewStep = useIsNewStep(step.step_id);
  const { data: latestVersion } = useRetrieveLatestAutomationVersion();

  return (
    <Stack
      style={{ borderColor: '#ECECED', borderWidth: 1, borderRadius: 5 }}
      bg={'#F6F6F6'}
      p={'xs'}
      gap={'xs'}
    >
      <Stack>
        <Group gap={'xs'} wrap={'nowrap'} align={'start'}>
          <StyledTextInput
            label="Name"
            value={conditionalDataOutput.name}
            placeholder="Output Name"
            onChange={(event) => {
              const value = event.currentTarget.value.replace(/[/\n]/g, '');
              onChangeDelay({ ...conditionalDataOutput, name: value });
            }}
            required={true}
            maxLength={STEP_FIELD_MAX_LENGTH}
            w={'100%'}
            error={!conditionalDataOutput.name && !isNewStep ? 'Name cannot be empty' : undefined}
          />
          <OutputPinButton step={step} outputId={conditionalDataOutput.output_id} />
          <StepFieldsRemoveButton onDelete={onDelete} />
        </Group>
        <StyledSwitch
          label="May be unavailable"
          description="Enables the branch-wide Mark All Outputs Unavailable action. That action marks every declared conditional output unavailable, so every output must enable this setting."
          checked={conditionalDataOutput.may_be_unavailable ?? false}
          onChange={(event) =>
            onChangeDelay({ ...conditionalDataOutput, may_be_unavailable: event.currentTarget.checked })
          }
        />
        <JsonSchemaRefSelect
          label="Variant output schema"
          description="Stable parent schema for every selected branch. It should accept all configured branch variants."
          schemas={latestVersion?.schemas ?? []}
          value={conditionalDataOutput.schema_ref}
          requiredValidationMode={SchemaValidationMode.Error}
          onChange={(schema_ref) => onChangeNoDelay({ ...conditionalDataOutput, schema_ref })}
        />
      </Stack>
    </Stack>
  );
};

export default ConditionalDataOutputFields;
